// frontend/src/hooks/usePendingReminders.js
//
// The page's pending follow-up reminders, from ONE request.
//
// Every lead card can show a reminder chip, and a grid holds dozens of cards,
// so a per-card GET is out (a performance rule for the lead grid). Instead a
// module-level store holds `lead_id -> scheduled_for` from a single
// `GET /follow-up-reminders/pending`, shared by every subscriber:
//
//   * fetched lazily when the first chip subscribes, and again when stale
//     (60s TTL) on subscribe or on window focus / tab becoming visible;
//   * concurrent refreshes share one in-flight request (dedupe);
//   * the reminder dialog and the chip UPSERT / REMOVE locally after their own
//     writes, so the chip moves immediately without waiting for a refetch;
//   * a write made while a fetch is in flight is replayed over that fetch's
//     result, so a slower pre-write response can never resurrect a cancelled
//     reminder or roll back a new time.
//
// Each card subscribes through `useSyncExternalStore` with a snapshot of ITS
// OWN value (a string or null), so a store change re-renders only the cards
// whose reminder actually changed.
//
// PENDING vs PASSED. The same response also carries recently SENT reminders
// (`status: "sent"` - sent in the last 7 days, lead still marked contacted,
// no newer reminder), which feed the "follow-up time passed" chip. They live
// in a SEPARATE map, so every pending-only consumer (`getPendingReminder`,
// `usePendingReminder`, the change dialog) is untouched by them. A row with no
// `status` is pending (an older server). Recording a new pending reminder
// drops the lead's passed entry - the pending chip replaces it, and the server
// stops returning the sent row once a newer reminder exists.
//
// A failed fetch is silent - the chip simply does not show - and logs nothing
// (the response carries nothing worth logging, and no PII may be logged).
//
// The store belongs to ONE signed-in user. It is keyed by user id
// (`bindPendingRemindersToUser`, called by AuthContext whenever the user id
// changes) and wiped by `resetPendingReminders()` on every logout path, so a
// second user on the same tab never sees the first user's reminders. A reset
// also bumps a generation counter, so a fetch still in flight for the previous
// user lands nowhere. A reset never fetches: on logout there is no session to
// fetch with, and the next subscriber (or focus) fetches for the new user.

import { useCallback, useEffect, useSyncExternalStore } from "react";

import followUpService from "../services/followUp";

export const PENDING_TTL_MS = 60 * 1000;

let entries = new Map(); // leadId -> scheduled_for (ISO string), PENDING only
let passed = new Map(); // leadId -> scheduled_for (ISO string) of a SENT reminder
let fetchedAt = 0;
let inflight = null;
let opsDuringFetch = null; // local writes to replay over an in-flight fetch
let generation = 0; // bumped on reset; a fetch from an older one is dropped
let ownerKey = null; // the user id the held entries belong to
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

// Ops act on both maps: {type: "upsert" | "remove" | "removePassed", leadId}.
function applyOp(pendingMap, passedMap, op) {
  if (op.type === "upsert") {
    pendingMap.set(op.leadId, op.scheduledFor);
    passedMap.delete(op.leadId);
  } else if (op.type === "removePassed") {
    passedMap.delete(op.leadId);
  } else {
    pendingMap.delete(op.leadId);
  }
}

function recordOp(op) {
  const nextPending = new Map(entries);
  const nextPassed = new Map(passed);
  applyOp(nextPending, nextPassed, op);
  entries = nextPending;
  passed = nextPassed;
  if (opsDuringFetch) opsDuringFetch.push(op);
  emit();
}

/**
 * Fetch the pending list unless a fresh copy is held. Concurrent callers
 * share one request. Never rejects.
 *
 * @param {{force?: boolean}} [options]
 * @returns {Promise<void>}
 */
export function refreshPendingReminders({ force = false } = {}) {
  if (inflight) return inflight;
  if (!force && fetchedAt && Date.now() - fetchedAt < PENDING_TTL_MS) {
    return Promise.resolve();
  }
  opsDuringFetch = [];
  const fetchGeneration = generation;
  const request = followUpService
    .listPendingReminders()
    .then((rows) => {
      if (fetchGeneration !== generation) return; // a reset happened meanwhile
      const nextPending = new Map();
      const nextPassed = new Map();
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row || !row.lead_id || !row.scheduled_for) return;
        const target = row.status === "sent" ? nextPassed : nextPending;
        target.set(String(row.lead_id), row.scheduled_for);
      });
      // The server never returns both for one lead; if it ever did, the
      // pending reminder wins.
      nextPending.forEach((_, leadId) => nextPassed.delete(leadId));
      (opsDuringFetch || []).forEach((op) => applyOp(nextPending, nextPassed, op));
      entries = nextPending;
      passed = nextPassed;
      fetchedAt = Date.now();
      emit();
    })
    .catch(() => {
      // Silent: no chip is better than an error on every card. The next
      // focus / subscribe retries (fetchedAt is untouched).
    })
    .finally(() => {
      // A reset already cleared these (and a newer fetch may own them now).
      if (fetchGeneration !== generation) return;
      inflight = null;
      opsDuringFetch = null;
    });
  inflight = request;
  return request;
}

/**
 * Forget every held reminder (logout, or a different user). Subscribed chips
 * hide at once; nothing is fetched until the next subscribe / focus.
 */
export function resetPendingReminders() {
  generation += 1;
  entries = new Map();
  passed = new Map();
  fetchedAt = 0;
  inflight = null;
  opsDuringFetch = null;
  ownerKey = null;
  emit();
}

/**
 * Key the store to the signed-in user: a different user id (or none) than the
 * one the held entries were fetched for resets it first.
 *
 * @param {string|number|null|undefined} userId
 */
export function bindPendingRemindersToUser(userId) {
  const key = userId == null ? null : String(userId);
  if (key === ownerKey) return;
  resetPendingReminders();
  ownerKey = key;
}

/** Record a reminder the user just created or moved. */
export function upsertPendingReminder(leadId, scheduledFor) {
  if (!leadId || !scheduledFor) return;
  recordOp({ type: "upsert", leadId: String(leadId), scheduledFor });
}

/** Forget a lead's reminder (cancelled, or found to be gone). */
export function removePendingReminder(leadId) {
  if (!leadId) return;
  recordOp({ type: "remove", leadId: String(leadId) });
}

/**
 * Forget a lead's passed (sent) reminder - its "contacted" mark changed, so
 * the "follow-up time passed" chip no longer applies.
 */
export function removePassedReminder(leadId) {
  if (!leadId) return;
  recordOp({ type: "removePassed", leadId: String(leadId) });
}

/**
 * A lead's feedback changed from `previous` to `next` (null = none): mirror
 * the server's cascade onto the held reminders, so the chips are honest
 * before the next fetch. The ONE copy of this rule on the client - the chip
 * and the details window both call it.
 *
 *   * the pending reminder is cancelled by the server when a "contacted" mark
 *     is undone (`feedback_undone`), and on converted / not relevant
 *     (api/v1/leads.py `_apply_lead_feedback`) - "scheduled" keeps it;
 *   * the passed chip ends on ANY move away from "contacted".
 *
 * @param {string} leadId
 * @param {string|null} previous
 * @param {string|null} next
 */
export function applyFeedbackChangeToReminders(leadId, previous, next) {
  if (!leadId) return;
  const prev = previous || null;
  const now = next || null;
  if (prev === now) return;
  if ((prev === "contacted" && !now) || now === "converted" || now === "not_interested") {
    removePendingReminder(leadId);
  }
  if (prev === "contacted") removePassedReminder(leadId);
}

/** The held value for one lead, without subscribing. */
export function getPendingReminder(leadId) {
  return (leadId && entries.get(String(leadId))) || null;
}

/** The held passed (sent) reminder instant for one lead, without subscribing. */
export function getPassedReminder(leadId) {
  return (leadId && passed.get(String(leadId))) || null;
}

// Window focus / visibility refetch, installed while anyone is subscribed.
function onFocus() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return;
  }
  refreshPendingReminders();
}

function subscribe(listener) {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== "undefined") {
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    }
  };
}

/** Test-only: forget everything. */
export function __resetPendingRemindersForTests() {
  generation += 1;
  entries = new Map();
  passed = new Map();
  fetchedAt = 0;
  inflight = null;
  opsDuringFetch = null;
  ownerKey = null;
  listeners.clear();
}

/**
 * The pending reminder instant (ISO string) for one lead, or null.
 * Subscribing starts (or reuses) the shared fetch.
 *
 * @param {string|null} leadId
 * @returns {string|null}
 */
export default function usePendingReminder(leadId) {
  const key = leadId ? String(leadId) : null;
  const getSnapshot = useCallback(
    () => (key ? entries.get(key) || null : null),
    [key],
  );
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (key) refreshPendingReminders();
  }, [key]);

  return value;
}

/**
 * The scheduled_for (ISO string) of the lead's recently SENT reminder - the
 * "follow-up time passed" chip - or null. Shares the one fetch with
 * `usePendingReminder`; a card that uses both still makes no extra request.
 *
 * @param {string|null} leadId
 * @returns {string|null}
 */
export function usePassedReminder(leadId) {
  const key = leadId ? String(leadId) : null;
  const getSnapshot = useCallback(
    () => (key ? passed.get(key) || null : null),
    [key],
  );
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (key) refreshPendingReminders();
  }, [key]);

  return value;
}
